import { createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import { clearScrollRestorationStorage } from "utils/scrollRestorationStorage";
import { logOut } from "./AuthSlice";

export type layout_types =  'toggle-desktop-menu' | null;
export type ai_mcat_chat_status =  'open' | 'closed' | 'hidden';

export interface PlannerEventDialogState {
    open: boolean;
    /** Event type label for modal chrome; empty when never opened this session */
    type: string;
    eventId: string | number | null;
}

export interface PlannerViewState {
    eventDialog: PlannerEventDialogState;
    /** ISO YYYY-MM-DD local calendar day; null when none selected */
    mobileSelectedDate: string | null;
    /** StudyPlannerEventId of expanded mobile schedule card; null when none */
    expandedEventId: number | null;
}

const plannerViewInitial: PlannerViewState = {
    eventDialog: {
        open: false,
        type: "",
        eventId: null,
    },
    mobileSelectedDate: null,
    expandedEventId: null,
};

interface LayoutState {
    layoutMode: layout_types;
    aiMCATChatDrawerStatus: ai_mcat_chat_status;
    toggleAiMCATDrawerExternally: boolean;
    toggleMobileMenu: boolean;
    isShowACTPopup: boolean;
    isIframeRefreshing: boolean;
    iframeModal: {
        isOpen: boolean;
        url: string;
        title?: string;
    };
    plannerView: PlannerViewState;
}

const initialState: LayoutState = {
    layoutMode: null,
    aiMCATChatDrawerStatus: 'hidden',
    toggleAiMCATDrawerExternally: false,
    toggleMobileMenu: false,
    isShowACTPopup: false,
    isIframeRefreshing: false,
    iframeModal: {
        isOpen: false,
        url: '',
        title: 'Content'
    },
    plannerView: plannerViewInitial,
};

export const layoutSlice = createSlice({
    name: "layout", 
    initialState,
    reducers: {
        setLayout: (state, action: PayloadAction<LayoutState["layoutMode"]>) => {
            state.layoutMode = action.payload;
        },
        setAIMCATChatStatus: (state, action: PayloadAction<ai_mcat_chat_status>) => {
            state.aiMCATChatDrawerStatus = action.payload;
        },
        setToggleAiMCATDrawerExternally: (state, action: PayloadAction<boolean>) => {
            state.toggleAiMCATDrawerExternally = action.payload;
        },
        setToggleMobileMenu: (state, action: PayloadAction<boolean>) => {
            state.toggleMobileMenu = action.payload;
        },
        setIsShowACTPopup: (state, action: PayloadAction<boolean>) => {
            state.isShowACTPopup = action.payload;
        },
        openIframeModal: (state, action: PayloadAction<{ url: string; title?: string }>) => {
            state.iframeModal.isOpen = true;
            state.iframeModal.url = action.payload.url;
            state.iframeModal.title = action.payload.title || 'Content';
        },
        closeIframeModal: (state) => {
            state.iframeModal.isOpen = false;
            state.iframeModal.url = '';
        },
        setIsIframeRefreshing: (state, action: PayloadAction<boolean>) => {
            state.isIframeRefreshing = action.payload;
        },
        toggleEventDialog: (state, action: PayloadAction<{ type: string; eventId?: string | number | null }>) => {
            state.plannerView.eventDialog = {
                open: !state.plannerView.eventDialog.open,
                type: action.payload.type,
                eventId: action.payload.eventId ?? null,
            };
        },
        closeEventDialog: (state) => {
            state.plannerView.eventDialog.open = false;
        },
        /** Resets desktop event dialog only; preserves mobile date / expanded card */
        resetPlannerView: (state) => {
            state.plannerView.eventDialog = {
                open: plannerViewInitial.eventDialog.open,
                type: plannerViewInitial.eventDialog.type,
                eventId: plannerViewInitial.eventDialog.eventId,
            };
        },
        setMobileSelectedDate: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.plannerView.mobileSelectedDate = action.payload;
            if (action.payload === null) {
                state.plannerView.expandedEventId = null;
            }
        },
        setExpandedEventId: (
            state,
            action: PayloadAction<number | null>
        ) => {
            state.plannerView.expandedEventId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logOut, (state) => {
            clearScrollRestorationStorage();
            state.plannerView = { ...plannerViewInitial };
        });
    },
});


export const {setLayout, setAIMCATChatStatus, setToggleAiMCATDrawerExternally, setToggleMobileMenu, openIframeModal, closeIframeModal, setIsShowACTPopup, setIsIframeRefreshing, toggleEventDialog, closeEventDialog, resetPlannerView, setMobileSelectedDate, setExpandedEventId} = layoutSlice.actions;
export default layoutSlice.reducer;
